import React, { useState, useEffect } from 'react';
import { auth, db, storage } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
// import './Steps.css';
import { useTranslation } from 'react-i18next';
import { logger } from '../../utils/logger';

interface Step1CompleteIdProps {
  onComplete: (step: string) => void;
}

type IdInfo = {
  type: string;
  number: string;
  frontImage: File | null;
  backImage: File | null;
};

type ImageSide = 'frontImage' | 'backImage';

const Step1CompleteId: React.FC<Step1CompleteIdProps> = ({ onComplete }) => {
  const { t } = useTranslation();
  const [idInfo, setIdInfo] = useState<IdInfo>({
    type: '',
    number: '',
    frontImage: null,
    backImage: null
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [frontPreview, setFrontPreview] = useState<string | null>(null);
  const [backPreview, setBackPreview] = useState<string | null>(null);

  useEffect(() => {
    logger.info('Step1CompleteId', 'Componente montado');
    return () => logger.info('Step1CompleteId', 'Componente desmontado');
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    logger.info('Step1CompleteId', `Cambio en ${name}`, value);
    setIdInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, side: ImageSide) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        logger.info('Step1CompleteId', `Imagen seleccionada (${side})`, file);
        setIdInfo(prev => ({
          ...prev,
          [side]: file
        }));
        // Crear vista previa
        const reader = new FileReader();
        reader.onloadend = () => {
          if (side === 'frontImage') {
            setFrontPreview(typeof reader.result === 'string' ? reader.result : null);
          } else {
            setBackPreview(typeof reader.result === 'string' ? reader.result : null);
          }
        };
        reader.readAsDataURL(file);
        setError('');
      } else {
        setError(t('onboarding.identity.error.invalidImage'));
        logger.error('Step1CompleteId', 'Archivo no es imagen');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    logger.info('Step1CompleteId', 'Enviando formulario', idInfo);
    try {
      if (!idInfo.type || !idInfo.number || !idInfo.frontImage || !idInfo.backImage) {
        throw new Error(t('onboarding.identity.error.requiredFields'));
      }
      if (!auth.currentUser) {
        setError(t('onboarding.identity.error.unauthenticated'));
        setLoading(false);
        logger.error('Step1CompleteId', 'Usuario no autenticado');
        return;
      }

      // Subir imágenes a Storage
      const timestamp = Date.now();
      const frontImageRef = ref(storage, `users/${auth.currentUser.uid}/id/front_${timestamp}`);
      const backImageRef = ref(storage, `users/${auth.currentUser.uid}/id/back_${timestamp}`);

      // Subir las imágenes con metadata
      const frontMetadata = {
        contentType: idInfo.frontImage.type,
        customMetadata: {
          'uploadedBy': auth.currentUser.uid,
          'documentType': idInfo.type,
          'documentNumber': idInfo.number
        }
      };

      const backMetadata = {
        contentType: idInfo.backImage.type,
        customMetadata: {
          'uploadedBy': auth.currentUser.uid,
          'documentType': idInfo.type,
          'documentNumber': idInfo.number
        }
      };

      // Subir las imágenes
      const [frontUploadResult, backUploadResult] = await Promise.all([
        uploadBytes(frontImageRef, idInfo.frontImage, frontMetadata),
        uploadBytes(backImageRef, idInfo.backImage, backMetadata)
      ]);

      // Obtener las URLs
      const [frontImageUrl, backImageUrl] = await Promise.all([
        getDownloadURL(frontUploadResult.ref),
        getDownloadURL(backUploadResult.ref)
      ]);

      // Actualizar documento del usuario
      await updateDoc(doc(db, 'users', auth.currentUser.uid), {
        identification: {
          type: idInfo.type,
          number: idInfo.number,
          frontImageUrl,
          backImageUrl,
          verificationStatus: 'pending',
          updatedAt: new Date().toISOString()
        }
      });

      logger.info('Step1CompleteId', 'Onboarding de identidad completado');
      onComplete('id');
    } catch (error: any) {
      logger.error('Step1CompleteId', 'Error en el proceso', error);
      setError(error.message || t('onboarding.identity.error.unknown'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto p-8 bg-white rounded-[20px_40px_20px_40px] shadow-2xl border border-white/40 animate-fadeIn font-['Montserrat']">
      <h2 className="text-3xl font-bold text-red-600 mb-4 text-center">{t('onboarding.identity.title')}</h2>
      <p className="text-gray-600 text-center mb-8 max-w-2xl mx-auto">
        {t('onboarding.identity.description')}
      </p>
      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start w-full">
        {/* Columna izquierda: datos */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <label htmlFor="type" className="text-gray-700 font-medium">{t('onboarding.identity.documentType')}</label>
            <select
              id="type"
              name="type"
              value={idInfo.type}
              onChange={handleChange}
              required
              className="w-full p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/10"
            >
              <option value="">{t('onboarding.identity.selectDocumentType')}</option>
              <option value="dni">{t('onboarding.identity.dni')}</option>
              <option value="passport">{t('onboarding.identity.passport')}</option>
              <option value="residency">{t('onboarding.identity.residency')}</option>
              <option value="driver_license">{t('onboarding.identity.driverLicense')}</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="number" className="text-gray-700 font-medium">{t('onboarding.identity.documentNumber')}</label>
            <input
              type="text"
              id="number"
              name="number"
              value={idInfo.number}
              onChange={handleChange}
              placeholder={t('onboarding.identity.documentNumberPlaceholder')}
              required
              className="w-full p-4 border-2 border-gray-200 rounded-xl text-base transition-all duration-300 bg-white focus:outline-none focus:border-red-500 focus:shadow-lg focus:shadow-red-500/10"
            />
          </div>
        </div>
        {/* Columna derecha: uploads */}
        <div className="flex flex-col gap-8">
          <div className="flex flex-col gap-2">
            <label className="text-gray-700 font-medium mb-3">{t('onboarding.identity.documentFront')}</label>
            <div className="w-full">
              <label className="w-full block cursor-pointer">
                <div className="border-2 border-dashed border-gray-200 rounded-xl bg-white min-h-32 flex items-center justify-center flex-col gap-2 transition-all duration-200 hover:border-red-500 relative w-full">
                  {frontPreview ? (
                    <div className="m-0">
                      <img src={frontPreview} alt={t('onboarding.identity.documentFrontPreviewAlt')} className="max-w-full max-h-24 object-contain rounded-lg" />
                    </div>
                  ) : (
                    <>
                      <svg width="38" height="38" fill="none" stroke="#d60e3c" strokeWidth="2" viewBox="0 0 24 24" className="text-red-500">
                        <rect x="3" y="7" width="18" height="13" rx="2"/>
                        <path d="M16 3v4M8 3v4M3 11h18"/>
                      </svg>
                      <span className="text-gray-400 text-sm">{t('onboarding.identity.documentFront')}</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'frontImage')}
                    required
                    className="hidden"
                  />
                </div>
              </label>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-gray-700 font-medium mb-3">{t('onboarding.identity.documentBack')}</label>
            <div className="w-full">
              <label className="w-full block cursor-pointer">
                <div className="border-2 border-dashed border-gray-200 rounded-xl bg-white min-h-32 flex items-center justify-center flex-col gap-2 transition-all duration-200 hover:border-red-500 relative w-full">
                  {backPreview ? (
                    <div className="m-0">
                      <img src={backPreview} alt={t('onboarding.identity.documentBackPreviewAlt')} className="max-w-full max-h-24 object-contain rounded-lg" />
                    </div>
                  ) : (
                    <>
                      <svg width="38" height="38" fill="none" stroke="#d60e3c" strokeWidth="2" viewBox="0 0 24 24" className="text-red-500">
                        <rect x="3" y="7" width="18" height="13" rx="2"/>
                        <path d="M16 3v4M8 3v4M3 11h18"/>
                      </svg>
                      <span className="text-gray-400 text-sm">{t('onboarding.identity.documentBack')}</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'backImage')}
                    required
                    className="hidden"
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
        {/* Fin columnas */}
        <div className="col-span-1 lg:col-span-2 mt-10 flex flex-col items-center w-full">
          {error && <div className="w-full max-w-lg text-red-600 bg-red-100 border border-red-200 rounded-lg px-4 py-3 text-center mb-6">{t(error)}</div>}
          <button 
            type="submit" 
            disabled={loading} 
            className="mt-6 min-w-56 px-8 py-4 bg-red-500 text-white rounded-xl font-semibold transition-all duration-300 hover:bg-red-600 hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? t('common.saving') : t('common.saveAndContinue')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step1CompleteId; 